import { useCallback, useEffect, useRef } from 'react';

type Abortable = { abort: (reason?: string) => void };

/**
 * Tracks dispatched async thunks and aborts any still in flight when the
 * calling component unmounts (e.g. the user navigates away from the page),
 * or when `resetKey` changes (e.g. the user switches to another client).
 *
 * Usage: `trackAbort(dispatch(fetchClients()))`
 */
export const useAbortOnUnmount = (resetKey?: unknown) => {
  const trackedRef = useRef<Set<Abortable>>(new Set());

  useEffect(() => {
    const tracked = trackedRef.current;
    return () => {
      tracked.forEach((abortable) => abortable.abort());
      tracked.clear();
    };
  }, [resetKey]);

  return useCallback(<T extends Abortable>(abortable: T): T => {
    trackedRef.current.add(abortable);
    return abortable;
  }, []);
};
